// ConfirmCancelEventPopUp.tsx
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Autocomplete, Box, Button, Dialog, IconButton, TextField, Typography } from "@mui/material";

import { colors } from "../../../theme/palette";
import { CloseDialogButtonIcon } from "../../../assets/icons/MUI/EventsIcons";
import { getCancelReasons } from "../../../api/events";

interface CancelReason {
  id: number;
  title: string;
}

interface CancelEventState {
  isCanceled: boolean;
  isPopUpOpen: boolean;
  reason: CancelReason | null;
  comment: string | null;
}

interface ConfirmCancelEventPopUpProps {
  openDialog: boolean;
  handleCloseDialog: () => void;
  handleCancelEvent: () => void;
  cancelEventState: CancelEventState;
  setCancelEventState: (state: CancelEventState | ((prev: CancelEventState) => CancelEventState)) => void;
}

const ConfirmCancelEventPopUp: React.FC<ConfirmCancelEventPopUpProps> = ({
  openDialog,
  handleCloseDialog,
  handleCancelEvent,
  cancelEventState,
  setCancelEventState,
}) => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  // Fetch cancel reasons and transform titles based on current language
  const { data: cancelReasons, isLoading: isCancelReasonsLoading } = useQuery({
    queryKey: ["cancelReasons"],
    queryFn: async () => {
      const reasons = await getCancelReasons();
      return reasons.map((reason: { id: number; titleEn: string; titleAr: string }) => ({
        id: reason.id,
        title: i18n.language === "ar" ? reason.titleAr : reason.titleEn,
      }));
    },
  });

  const handleClose = () => {
    handleCloseDialog();
    setCancelEventState((prev) => ({ ...prev, reason: null, isPopUpOpen: false }));
    setError(null);
  };

  const handleReasonChange = (reason: CancelReason | null) => {
    setCancelEventState((prev) => ({ ...prev, reason }));
  };

  const handleCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCancelEventState((prev) => ({ ...prev, comment: e.target.value }));
  };

  const handleSubmit = () => {
    if (!cancelEventState.reason) {
      setError(t("pages.events.eventDetails.selectReasonError"));
      return;
    }
    setError(null);
    handleCancelEvent();
  };

  return (
    <Dialog
      open={openDialog}
      onClose={handleClose}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          width: "400px",
        },
      }}
    >
      <Typography
        variant="body16Bold"
        sx={{
          color: colors.Dark1000,
          display: "block",
          padding: "24px 0",
          textAlign: "center",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "24px",
          position: "relative",
        }}
      >
        {t("pages.events.eventDetails.cancelEvent")}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
        >
          <CloseDialogButtonIcon />
        </IconButton>
      </Typography>

      <Typography
        variant="body14SemiBold"
        sx={{
          color: colors.Dark1000,
          display: "block",
          padding: "0 24px",
        }}
      >
        {t("pages.events.eventDetails.needConfirmationToCancelEvent")}
      </Typography>

      <Typography
        variant="body14Regular"
        sx={{
          color: colors.Dark600,
          display: "block",
          padding: "0 24px",
          marginBottom: "8px",
        }}
      >
        {t("pages.events.eventDetails.chooseCancellationReason")}
      </Typography>

      <Autocomplete
        options={cancelReasons || []}
        loading={isCancelReasonsLoading}
        value={cancelEventState.reason}
        onChange={(_, option) => handleReasonChange(option)}
        getOptionLabel={(option) => (option ? option.title : "")}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{
          margin: "0 24px 14px",
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("pages.events.eventDetails.selectReason")}
            error={!!error}
            helperText={error}
          />
        )}
      />

      <Typography
        variant="body14Regular"
        sx={{
          color: colors.Dark800,
          display: "block",
          marginBottom: "6px",
          padding: "0 24px",
          fontWeight: 600,
        }}
      >
        {t("pages.events.eventDetails.comment")}
      </Typography>
      <TextField
        multiline
        rows={4}
        name="descriptionRules"
        value={cancelEventState.comment || ""}
        onChange={handleCommentChange}
        placeholder={t("pages.events.eventDetails.infoAboutCancelGame")}
        sx={{
          margin: "0 24px",
          "& .MuiInputBase-root": {
            borderRadius: "8px !important",
          },
        }}
        inputProps={{
          autoComplete: "new-password",
          form: {
            autoComplete: "off",
          },
        }}
      />

      <Box sx={{ padding: "24px" }}>
        <Button variant="primary-sm" onClick={handleSubmit} fullWidth>
          {t("pages.events.eventDetails.submit")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ConfirmCancelEventPopUp;
